import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { testAPIConnection, getDataSources } from "../../services/purecloud"; // Adjust path as needed
import { getStorageItem } from "../../services/applicationStorage";

interface CustomHeader {
  key: string;
  value: string;
}

interface ApiRetrievalData {
  apiUrl: string;
  authMethod: string;
  httpMethod: string;
  apiKey?: string;
  apiKeyHeader?: string;
  oauthClientId?: string;
  oauthClientSecret?: string;
  oauthTokenUrl?: string;
  oauthScope?: string;
  basicAuthUsername?: string;
  basicAuthPassword?: string;
  bearerToken?: string;
  customHeaders?: CustomHeader[];
}

interface ApiRetrievalProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  setSnackDetails?: (details: {
    open: boolean;
    message: React.ReactNode;
    severity: string;
  }) => void;
  setFormDataLoading?: (loading: boolean) => void;
}

const ApiRetrieval: React.FC<ApiRetrievalProps> = ({
  control,
  setValue,
  watch,
  setSnackDetails,
  setFormDataLoading,
}) => {
  const [isTestingConnection, setIsTestingConnection] =
    useState<boolean>(false);
  const [apiTestResponse, setApiTestResponse] = useState<any>(null);
  const [datasources, setDatasources] = useState<any[]>([]);
  const apiUrl = watch("apiRetrieval.apiUrl") || '';

  // Watch for changes in the form
  const [datasourceId, setDatasourceId] = useState("");

  useEffect(() => {
    loadDatasources();
  }, []);

  // Effect to set datasource when form data is loaded (for edit mode)
  useEffect(() => {
    const existingDatasource = watch("apiRetrieval.datasource");
    if (existingDatasource && datasources.length > 0) {
      setDatasourceId(existingDatasource);
      // Also populate apiRecordPath if it's empty (for existing jobs that didn't have it)
      const existingApiRecordPath = watch("apiRetrieval.apiRecordPath");
      if (!existingApiRecordPath) {
        const selectedDatasource = datasources.find(
          (d) => d.name === existingDatasource
        );
        if (selectedDatasource?.apiRecordPath) {
          setValue("apiRetrieval.apiRecordPath", selectedDatasource.apiRecordPath);
        }
      }
    }
  }, [datasources, watch, setValue]);

  const loadDatasources = async (showNotification = false) => {
    try {
      const response = await getDataSources();
      console.log("Loaded datasources in ApiRetrieval:", response.datasources);
      setDatasources(response.datasources || []);
      if (showNotification && setSnackDetails) {
        setSnackDetails({
          open: true,
          message: <>Datasources reloaded successfully</>,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Failed to load datasources:", error);
      if (showNotification && setSnackDetails) {
        setSnackDetails({
          open: true,
          message: <>Failed to reload datasources</>,
          severity: "error",
        });
      }
    }
  };

  const handleTestConnection = async () => {
    // Get the datasource type for special handling
    const selectedDatasource = datasources.find((d) => d.name === datasourceId);
    const apiConfig = watch("apiRetrieval");

    if (!apiUrl || !datasourceId) {
      if (setSnackDetails) {
        setSnackDetails({
          open: true,
          message: <>API URL and Datasource are required</>,
          severity: "error",
        });
      }
      return;
    }

    setIsTestingConnection(true);
    if (setFormDataLoading) {
      setFormDataLoading(true);
    }

    try {
      const datasourceType = selectedDatasource?.type || "";

      const testConfig = {
        apiUrl: apiConfig.apiUrl,
        authMethod: selectedDatasource.authMethod,
        httpMethod: selectedDatasource.httpMethod || "GET",
        apiKey: selectedDatasource.apiKey?.key || "",
        apiKeyHeader: selectedDatasource.apiKey?.header || "X-API-Key",
        oauthClientId: selectedDatasource.oauth2?.clientId || "",
        oauthClientSecret: selectedDatasource.oauth2?.clientSecret || "",
        oauthTokenUrl:
          selectedDatasource.oauth2?.tokenUrl ||
          (datasourceType === "Zoho CRM"
            ? "https://accounts.zoho.com/oauth/v2/token"
            : ""),
        oauthScope: selectedDatasource.oauth2?.scope || "",
        basicAuthUsername: selectedDatasource.basicAuth?.username || "",
        basicAuthPassword: selectedDatasource.basicAuth?.password || "",
        bearerToken: selectedDatasource.bearerToken || "",
        customHeaders: selectedDatasource.customHeaders || [],
        datasourceType: datasourceType,
        // For Zoho CRM, use the stored refresh token from the datasource
        refreshToken:
          datasourceType === "Zoho CRM"
            ? selectedDatasource?.oauth2?.refreshToken
            : "",
        grantType: datasourceType === "Zoho CRM" ? "refresh_token" : "",
        orgId: getStorageItem("organization-id", true, sessionStorage),
        requestBody: watch("apiRetrieval.requestBody") || '',
      };

      console.log("Test config being sent:", testConfig);
      console.log(
        "Selected datasource:",
        datasources.find((d) => d.name === datasourceId)
      );

      const response = await testAPIConnection(testConfig);
      setApiTestResponse(response);

      if (setSnackDetails) {
        setSnackDetails({
          open: true,
          message: <>API connection test successful!</>,
          severity: "success",
        });
      }
    } catch (error: any) {
      console.error("API connection test failed:", error);
      setApiTestResponse(error);

      if (setSnackDetails) {
        setSnackDetails({
          open: true,
          message: (
            <>API connection test failed. {error?.message || 'Please check your configuration.'}</>
          ),
          severity: "error",
        });
      }
    } finally {
      setIsTestingConnection(false);
      if (setFormDataLoading) {
        setFormDataLoading(false);
      }
    }
  };

  function shouldShowRequestBody() {
    const selectedDatasource = datasources.find((d) => d.name === datasourceId);
    const httpMethod = selectedDatasource?.httpMethod;

    return selectedDatasource && httpMethod && httpMethod !== "GET";
  }

  return (
    <div>
      <h3 style={{ marginBottom: "20px" }}>
        API Configuration
      </h3>

      {
        <Controller
          name="apiRetrieval.apiUrl"
          control={control}
          rules={{ required: "API URL is required" }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="API URL *"
              variant="outlined"
              size="small"
              placeholder="https://api.example.com/data"
              error={!!error}
              helperText={error?.message}
              sx={{ width: "100%", mb: 3 }}
            />
          )}
        />
      } 

      <Box sx={{ display: "flex", flexGrow: 1, flexDirection: "row" }}>
        <FormControl sx={{ flexGrow: 1, mr: 2 }}>
          <InputLabel id="datasource-label">Datasource *</InputLabel>
          <Select
            value={datasourceId}
            onChange={(e) => {
              setDatasourceId(e.target.value);
              setValue("apiRetrieval.datasource", e.target.value);
              setApiTestResponse(null);
              const selectedDatasource = datasources.find(
                (d) => d.name === e.target.value
              );
              console.log(
                "Selected datasource for population:",
                selectedDatasource
              );
              if (selectedDatasource) {
                setValue(
                  "apiRetrieval.authMethod",
                  selectedDatasource.authMethod
                );
                setValue(
                  "apiRetrieval.httpMethod",
                  selectedDatasource.httpMethod || "GET"
                );
                setValue(
                  "apiRetrieval.apiKey",
                  selectedDatasource.apiKey?.key || ""
                );
                setValue(
                  "apiRetrieval.apiKeyHeader",
                  selectedDatasource.apiKey?.header || "X-API-Key"
                );
                setValue(
                  "apiRetrieval.bearerToken",
                  selectedDatasource.bearerToken || ""
                );
                setValue(
                  "apiRetrieval.basicAuthUsername",
                  selectedDatasource.basicAuth?.username || ""
                );
                setValue(
                  "apiRetrieval.basicAuthPassword",
                  selectedDatasource.basicAuth?.password || ""
                );
                setValue(
                  "apiRetrieval.apiDataFormat",
                  selectedDatasource.apiDataFormat || "json"
                );
                setValue(
                  "apiRetrieval.apiRecordPath",
                  selectedDatasource.apiRecordPath || ""
                );
                setValue(
                  "apiRetrieval.requestBody",
                  selectedDatasource.requestBody || ""
                );

                // Set OAuth fields for all datasource types
                setValue(
                  "apiRetrieval.oauthClientId",
                  selectedDatasource.oauth2?.clientId || ""
                );
                setValue(
                  "apiRetrieval.oauthClientSecret",
                  selectedDatasource.oauth2?.clientSecret || ""
                );
                setValue(
                  "apiRetrieval.oauthTokenUrl",
                  selectedDatasource.oauth2?.tokenUrl || ""
                );

                if (selectedDatasource.type !== "Zoho CRM") {
                  setValue(
                    "apiRetrieval.oauthScope",
                    selectedDatasource.oauth2?.scope || ""
                  );
                }
              }
            }}
            labelId="datasource-label"
            label="Datasource *"
          >
            {datasources.map((datasource) => (
              <MenuItem key={datasource.name} value={datasource.name}>
                {datasource.name} ({datasource.type})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="medium"
          type="button"
          onClick={() => loadDatasources(true)}
          sx={{ height: "56px", px: 2 }}
        >
          Reload
        </Button>
            </Box>

            {shouldShowRequestBody() && (
            <Box sx={{ display: "flex", flexGrow: 1, flexDirection: "row", mt: 3 }}>
              <Controller
                name="apiRetrieval.requestBody"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Request Body (JSON)"
                    variant="outlined"
                    multiline
                    rows={4}
                    size="small"
                    placeholder='{"key": "value"}'
                    sx={{ flexGrow: 1, mr: 2 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
              {datasourceId && (
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleTestConnection}
                  disabled={isTestingConnection}
                  sx={{ color: "white", backgroundColor: "#1b2c48", height: "fit-content", alignSelf: "flex-end", mb: "23px" }}
                >
                  {isTestingConnection ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                      Testing...
                    </>
                  ) : (
                    "Test API Connection"
                  )}
                </Button>
              )}
            </Box>
        )}

      {/* {datasourceId && (
          <Button
            variant="outlined"
            size="small"
            type="button"
            onClick={() => {
              const selectedDatasource = datasources.find(
                (d) => d.name === datasourceId
              );
              if (selectedDatasource && setSnackDetails) {
                setSnackDetails({
                  open: true,
                  message: (
                    <>
                      Datasource: {selectedDatasource.name} (
                      {selectedDatasource.type}) - Status: Active
                    </>
                  ),
                  severity: "info",
                });
              }
            }}
            sx={{ minWidth: "auto", px: 2 }}
          >
            Test
          </Button>
        )} */}

      {datasourceId && !shouldShowRequestBody() && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="medium"
            onClick={handleTestConnection}
            disabled={isTestingConnection}
            sx={{ color: "white", backgroundColor: "#1b2c48" }}
          >
            {isTestingConnection ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                Testing...
              </>
            ) : (
              "Test API Connection"
            )}
          </Button>
        </Box>
      )}

      {apiTestResponse && (
        <Box sx={{ mt: 3 }}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              backgroundColor: "#f5f5f5",
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: "#1b2c48" }}>
              API Test Response
            </Typography>
            <Box
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 2,
                maxHeight: "300px",
                overflow: "auto",
                fontFamily: "monospace",
                fontSize: "0.875rem",
              }}
            >
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(apiTestResponse, null, 2)}
              </pre>
            </Box>
          </Paper>
        </Box>
      )}
    </div>
  );
};

export default ApiRetrieval;
